import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { RootState } from "../store";
import { setDisplayTzPrefs } from "../store/slices/userPreferencesSlice";
import { httpClient } from "../lib/http-client";
import { logger } from "../services/logger.service";

/**
 * Final piece of the timezone workstream (e): a dismissible nudge when the user's ACCOUNT
 * display timezone (their personal pref) differs from the DEVICE's timezone.
 *
 * Fires ONLY in personal-mode (community-mode = the user opted into per-record display, so a
 * device mismatch is expected). Compares RESOLVED IANA ZONE STRINGS, never offsets. Does NOT
 * fire when the personal tz is unset (nothing to mismatch).
 *
 * "Switch" PERSISTS the account tz to public.user_preferences (the dedicated display field) —
 * NO digest side-effect (the digest reads crm_notification_prefs.timezone, a separate column).
 * Dismissal is keyed to the exact (account, device) pair in localStorage, so it stays dismissed
 * unless the mismatch genuinely changes (new device/zone) rather than nagging every load.
 */
const DISMISS_KEY = "tzMismatchBannerDismissed";

function getBrowserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

/**
 * Two IANA zones are display-EQUIVALENT when they yield the same wall-clock for the same
 * instant — true for ALIASES (Asia/Calcutta ≡ Asia/Kolkata) and same-rule zones
 * (America/New_York ≡ America/Toronto). We probe a WINTER and a SUMMER instant so a genuine
 * DST-rule difference (e.g. America/Phoenix vs America/Denver) is still caught. A raw string
 * compare would false-fire on aliases — which is exactly the bug this prevents.
 */
function zonesEquivalent(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    const at = (iso: string, tz: string) =>
      new Date(iso).toLocaleString("en-US", { timeZone: tz, hour12: false });
    return (
      at("2025-01-15T12:00:00Z", a) === at("2025-01-15T12:00:00Z", b) &&
      at("2025-07-15T12:00:00Z", a) === at("2025-07-15T12:00:00Z", b)
    );
  } catch {
    return a === b; // an invalid zone string → fall back to exact match
  }
}

export function TzMismatchBanner() {
  const userTz = useSelector((s: RootState) => s.userPreferencesReducer.userTimezone);
  const mode = useSelector((s: RootState) => s.userPreferencesReducer.userTimezoneMode);
  const instanceTz = useSelector((s: RootState) => s.userPreferencesReducer.instanceTimezone);
  const dispatch = useDispatch();
  const [hidden, setHidden] = useState(false);
  const [switching, setSwitching] = useState(false);

  const device = getBrowserTz();
  const pairKey = `${userTz}|${device}`;
  const dismissedForPair = (() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === pairKey;
    } catch {
      return false;
    }
  })();

  // personal-mode only · personal tz set · zones NOT equivalent (aliases like Calcutta≡Kolkata
  // don't count) · not dismissed-for-this-pair.
  const show =
    mode === "personal" &&
    !!userTz &&
    !!device &&
    !zonesEquivalent(userTz, device) &&
    !dismissedForPair &&
    !hidden;
  if (!show) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, pairKey);
    } catch {
      /* non-fatal */
    }
    setHidden(true);
  };

  const switchToDevice = async () => {
    setSwitching(true);
    try {
      // PERSIST to the dedicated display field — no digest impact.
      await httpClient.put("/api/v1/user-preferences", {
        timezone: device,
        timezoneMode: "personal",
      });
      dispatch(
        setDisplayTzPrefs({
          userTimezone: device,
          userTimezoneMode: "personal",
          instanceTimezone: instanceTz,
        }),
      );
      setHidden(true);
    } catch (err) {
      logger.error("[TzMismatchBanner] switch failed", err);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2"
    >
      <SkyText size={200}>
        Your account timezone (<strong>{userTz}</strong>) differs from this device (
        <strong>{device}</strong>).
      </SkyText>
      <div className="flex shrink-0 items-center gap-2">
        <SkyButton
          size="small"
          onClick={switchToDevice}
          disabled={switching}
        >
          Switch to {device}
        </SkyButton>
        <SkyButton
          size="small"
          appearance="subtle"
          onClick={dismiss}
          disabled={switching}
        >
          Dismiss
        </SkyButton>
      </div>
    </div>
  );
}
